import { zodResolver } from '@hookform/resolvers/zod'
import BigNumber from 'bignumber.js'
import { useEffect, useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { PositionOverview } from '../types'
import { getBorrowOptions } from './assets'
import { calculateUpdatePositionOverview } from './calculateUpdatedPositionOverview'
import { createBorrowObjectives } from './createBorrowObjectives'
import { filterRelatedCampaigns } from './filterRelatedCampaigns'
import { getBorrowDialogFormValidator, getFormFieldsForBorrowDialog } from './form'
import { getNativeAssetInfo } from '@/config/chain/utils/getNativeAssetInfo'
import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import {
  AssetInputSchema,
  DialogFormNormalizedData,
  useDebouncedDialogFormValues,
} from '@/features/dialogs/common/logic/form'
import { FormFieldsForDialog, PageState, PageStatus } from '@/features/dialogs/common/types'
import { useOngoingCampaignsQuery } from '@/features/spark-rewards/data/useOngoingCampaignsQuery'
import { useAaveDataLayer } from '@/features/sparklend/common/data/market-info/aave-data-layer/useAaveDataLayer'
import { EPOCH_LENGTH } from '@/features/sparklend/common/data/market-info/consts'
import { Reserve } from '@/features/sparklend/common/data/market-info/marketInfo'
import { useMarketInfo } from '@/features/sparklend/common/data/market-info/useMarketInfo'
import { useMarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { RiskAcknowledgementInfo } from '@/features/sparklend/common/utils/liquidation-risk-warning/types'
import { useLiquidationRiskWarning } from '@/features/sparklend/common/utils/liquidation-risk-warning/useLiquidationRiskWarning'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'
import { SparkRewardCampaign } from '@/ui/molecules/spark-reward-pill/SparkRewardPill'

export interface UseBorrowDialogOptions {
  reserve: Reserve
  initialBorrowToken: SparkLendToken
}

export interface UseBorrowDialogResult {
  dialogTitle: string
  borrowOptions: TokenWithBalance[]
  assetsToBorrowFields: FormFieldsForDialog
  borrowAsset: TokenWithValue
  outputAsset: TokenWithValue
  actionsViewState: ActionsViewState
  pageStatus: PageStatus
  form: UseFormReturn<AssetInputSchema>
  currentPositionOverview: PositionOverview
  updatedPositionOverview: PositionOverview | undefined
  riskAcknowledgement: RiskAcknowledgementInfo
  relatedCampaigns: SparkRewardCampaign[]
}

export function useBorrowDialog({ reserve, initialBorrowToken }: UseBorrowDialogOptions): UseBorrowDialogResult {
  const chainId = useChainId()
  const { aaveData } = useAaveDataLayer({ chainId })
  const { marketInfo } = useMarketInfo({ chainId })
  const { marketInfo: marketInfoIn1Epoch } = useMarketInfo({ timeAdvance: EPOCH_LENGTH, chainId })
  const walletInfo = useMarketWalletInfo({ chainId })
  const nativeAssetInfo = getNativeAssetInfo(marketInfo.chainId)
  const { isInSandbox } = useSandbox()
  const ongoingCampaignsResult = useOngoingCampaignsQuery()
  const borrowPosition = marketInfo.findOnePositionByToken(reserve.token)

  const [pageStatus, setPageStatus] = useState<PageState>('form')

  const form = useForm<AssetInputSchema>({
    resolver: zodResolver(getBorrowDialogFormValidator(marketInfo, reserve)),
    defaultValues: {
      symbol: initialBorrowToken.symbol,
      value: '',
    },
    mode: 'onChange',
  })

  const borrowOptions = getBorrowOptions({
    reserve,
    marketInfo,
    walletInfo,
    nativeAssetInfo,
    isInSandbox,
  })
  const {
    debouncedFormValues: outputToken,
    isDebouncing,
    isFormValid,
  } = useDebouncedDialogFormValues({
    form,
    marketInfo,
  })
  const tokenToBorrow: DialogFormNormalizedData = {
    token: outputToken.token.symbol === nativeAssetInfo.nativeAssetSymbol ? outputToken.token : reserve.token,
    value: outputToken.value,
    position: marketInfo.findOnePositionByToken(reserve.token),
    reserve,
    isMaxSelected: outputToken.isMaxSelected,
  }

  const assetsToBorrowFields = getFormFieldsForBorrowDialog({
    form,
    reserve,
    marketInfo,
    marketInfoIn1Epoch,
    walletInfo,
  })
  const actions = createBorrowObjectives({
    outputToken,
    reserve,
  })

  const currentPositionOverview: PositionOverview = {
    healthFactor: marketInfo.userPositionSummary.healthFactor ?? NO_DEBT_HEALTH_FACTOR,
    debt: borrowPosition.borrowBalance,
    borrowAPY: borrowPosition.reserve.variableBorrowApy,
  }
  const updatedPositionOverview = calculateUpdatePositionOverview({
    tokenToBorrow,
    marketInfo,
    aaveData,
    nativeAssetInfo,
    currentPositionOverview,
  })

  const { riskAcknowledgement, disableActionsByRisk } = useLiquidationRiskWarning({
    type: 'liquidation-warning-borrow',
    isFormValid,
    currentHealthFactor: currentPositionOverview.healthFactor,
    updatedHealthFactor: updatedPositionOverview?.healthFactor,
  })

  const actionsEnabled = tokenToBorrow.value.gt(0) && isFormValid && !isDebouncing && !disableActionsByRisk

  const relatedCampaigns = filterRelatedCampaigns({
    ongoingCampaignsResult,
    token: reserve.token,
    chainId,
  })

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives: actions,
    context: {
      marketInfo,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [outputToken.token.symbol, outputToken.value.toString(), outputToken.isMaxSelected, resetActions])

  const dialogTitle =
    reserve.token.symbol === outputToken.token.symbol
      ? `Borrow ${outputToken.token.symbol}`
      : `Borrow ${reserve.token.symbol} and swap to ${outputToken.token.symbol}`

  return {
    dialogTitle,
    borrowOptions,
    assetsToBorrowFields,
    borrowAsset: tokenToBorrow,
    outputAsset: outputToken,
    actionsViewState,
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    form,
    currentPositionOverview,
    updatedPositionOverview,
    riskAcknowledgement,
    relatedCampaigns,
  }
}

const NO_DEBT_HEALTH_FACTOR = new BigNumber(Number.POSITIVE_INFINITY)
